import Rectangle = PIXI.Rectangle;
import { MapList } from '../Config/Levels';
import Core from '../Core/Core';
import { Rng } from './Rng';

export const getRandom = (min: number, max: number, seed?: string) => {
    const generator = new Rng(seed, true);
    const generate = seed ? generator.generate.bind(generator) : Math.random;
    return Math.floor(generate() * (max - min + 1)) + min;
};
export const getRandomFloat = (min: number, max: number) => {
    const interval = 0.001;
    const r = Math.floor((Math.random() * (max - min + interval)) / interval);
    return r * interval + min;
};

export function formatNumberString(value: number, minimumIntegerDigits: number = 2) {
    return value.toLocaleString(undefined, { minimumIntegerDigits, useGrouping: false });
}

export function shuffleArray(array: Array<unknown>) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
}

export function arrayEqual(a: Array<unknown>, b: Array<unknown>) {
    if (a.length !== b.length) {
        return false;
    }
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return false;
        }
    }
    return true;
}

export function lerp(a: number, b: number, t: number) {
    return (1 - t) * a + t * b;
}

/**
 * Return a random Id with numbers and letters.
 * @param length The length of the resulting string once the current string has been padded
 * @param padString The string to pad the current string with
 */
export const getRandomId = (length: number, padString: string = '0'): string => {
    const pad: string = Array(length)
        .fill(padString)
        .join('');
    const rand: string = getRandom(0, Number.MAX_SAFE_INTEGER).toString(36);
    return `${pad}${rand}`.slice(-length);
};

export const scaleToFit = (w1: number, h1: number, w2: number, h2: number, padding?: number) => {
    const p = padding || 0;
    let scale = (w2 - p) / w1;
    if (h1 * scale > h2 - p) {
        scale = (h2 - p) / h1;
    }
    return scale;
};

export function createFrameList(prefix: string, count: number) {
    const frames = [];
    const assets = PIXI.utils.TextureCache;
    for (let i = 0; i < count; i++) {
        frames.push(assets[`${prefix}${formatNumberString(i + 1, 4)}`]);
    }
    return frames;
}

export const createOutlineText = (
    str: string,
    bounds: Rectangle,
    font: string,
    size: number = 30,
    lineGap: number = 28,
    color: number = 0xffffff,
    outline: number = 0x2a3a71,
    stroke: number = 6
) => {
    let txt;
    if (Core.local.languageKey === 'ru') {
        txt = new PIXI.Text(str, {
            align: 'center',
            fill: color,
            fontFamily: 'Marmelad',
            fontStyle: 'Bold',
            fontSize: size - 3,
            lineHeight: lineGap,
            stroke: outline,
            strokeThickness: stroke,
            lineJoin: 'round',
            wordWrap: true,
            wordWrapWidth: bounds.width * 0.9,
        });
    } else {
        txt = new PIXI.Text(str, {
            align: 'center',
            fill: color,
            fontFamily: font,
            fontSize: size,
            lineHeight: lineGap,
            stroke: outline,
            strokeThickness: stroke,
            lineJoin: 'round',
            wordWrap: true,
            wordWrapWidth: bounds.width * 0.9,
        });
    }

    txt.x = bounds.x;
    txt.y = bounds.y;
    txt.anchor.set(0.5);
    bounds = null;
    return txt;
};

export const createPixiText = (
    str: string,
    bounds: Rectangle,
    font: string,
    size: number = 30,
    lineGap: number = 28,
    color: number = 0x006eba,
    alignment: string = 'center'
) => {
    let txt;
    if (Core.local.languageKey === 'ru') {
        txt = new PIXI.Text(str, {
            align: alignment,
            fill: color,
            fontFamily: 'Marmelad',
            fontStyle: 'Bold',
            fontSize: size - 3,
            lineHeight: lineGap,
            wordWrap: true,
            wordWrapWidth: bounds.width * 0.9,
        });
    } else {
        txt = new PIXI.Text(str, {
            align: alignment,
            fill: color,
            fontFamily: font,
            fontSize: size,
            lineHeight: lineGap,
            wordWrap: true,
            wordWrapWidth: bounds.width * 0.9,
        });
    }

    txt.x = bounds.x;
    txt.y = bounds.y;
    txt.anchor.set(0.5);
    bounds = null;
    return txt;
};

export const createBitmapText = (
    str: string,
    bounds: Rectangle,
    font: string,
    size: number = 26,
    color?: number
) => {
    const txt = new PIXI.extras.BitmapText(str, {
        align: 'left',
        font: { size, name: font },
    });

    txt.scale.set(scaleToFit(txt.width, txt.height, bounds.width, bounds.height));

    txt.x = bounds.x;
    txt.y = bounds.y;
    if (color) {
        txt.tint = color;
    }

    bounds = null;

    return txt;
};

export const getBoardJson = (id: string) => {
    const [mapName, levelNumber] = id.split('-');
    const bundleId = `${mapName}-levels`;
    return Core.gameCache.jsons[bundleId][parseInt(levelNumber, 10) - 1];
};

export function equalArrays<T>(arrs: T[][]) {
    let equals = true;
    const len = arrs.length;
    const len0 = arrs[0].length;
    const unequalLengths = arrs.filter(arr => arr.length !== len0).length;
    if (unequalLengths > 0) {
        return false;
    }
    arrs[0].forEach((_, index) => {
        for (let i = 1; i < len; i++) {
            if (!arrs[i].includes(arrs[0][index])) {
                equals = false;
                break;
            }
        }
    });

    return equals;
}

export function matchArrayElement<T>(arrs: T[][]) {
    let match = false;
    const len = arrs.length;
    arrs[0].forEach((_, index) => {
        for (let i = 1; i < len; i++) {
            if (arrs[i].includes(arrs[0][index])) {
                match = true;
                break;
            }
        }
    });

    return match;
}

export function getBrowserLanguage() {
    let lang = 'en';
    if (window.navigator.languages && navigator.languages.length) {
        lang = navigator.languages[0];
    } else {
        // @ts-ignore
        lang = navigator.userLanguage || navigator.language || navigator.browserLanguage || 'en';
    }
    if (lang === 'pt-BR' || lang === 'pt_BR') {
        lang = 'pt-BR';
    } else {
        lang = lang.substring(0, 2);
    }

    return lang;
}

export const getLevelConfig = (id: string) => {
    const levelStr = id.split('-').shift();
    let levelNumStr = id.split('-').pop() || '';
    // TODO get final level count
    // off for now while the same levels are in both
    levelNumStr = (parseInt(levelNumStr, 10) + 50 * MapList.indexOf(levelStr)).toString(10);
    while (levelNumStr.split('').length < 3) {
        levelNumStr = '0' + levelNumStr;
    }

    return 'level-' + levelNumStr;
    // return 'level-gold1';
};

export const createRect = (bounds: Rectangle, fill: number, line: number, lineWidth: number) => {
    const g = new PIXI.Graphics /*true*/();
    drawRect(g, bounds, fill, line, lineWidth);
    return g;
};

export const drawRect = (
    g: PIXI.Graphics,
    bounds: Rectangle,
    fill: number,
    line: number,
    lineWidth: number
) => {
    g.beginFill(fill);
    g.lineStyle(lineWidth, line);
    g.drawRect(bounds.x, bounds.y, bounds.width, bounds.height);
};

// use from board container
export const getOffsetForLayer = (layer: number, tileSize: [number, number]) => {
    return -(tileSize[0] * 0.1) * layer;
};
